import { useState } from 'react';
import { App, Button, Card, Input, Modal, Space, Tooltip, Typography } from 'antd';
import {
  ClearOutlined,
  DeleteOutlined,
  KeyOutlined,
  LockOutlined,
  PoweroffOutlined,
  SearchOutlined,
  SyncOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuickCommand } from '@/hooks/useDevices';
import type { DeviceDetailDto } from '@/types/device';

const { Text } = Typography;

interface Props {
  device: DeviceDetailDto;
  onCommandSent: () => void;
}

/**
 * Mapeo CommandType backend (numeros) -> labels en español + icono + color + flag de "destructivo"
 *
 * Los comandos destructivos (Wipe, ResetPassword) se marcan en rojo y abren un confirm dialog.
 * Los que requieren parametros (ResetPassword, ClearAppData, StartLostMode) abren un modal con form.
 */
const COMMAND_DEFS = [
  { key: 0, label: 'Bloquear', icon: <LockOutlined />, dangerous: false, color: undefined, needsParams: false, tooltip: 'Bloquea la pantalla del device.' },
  { key: 1, label: 'Reiniciar', icon: <PoweroffOutlined />, dangerous: false, color: undefined, needsParams: false, tooltip: 'Apaga y prende el device.' },
  { key: 2, label: 'Resetear PIN', icon: <KeyOutlined />, dangerous: true, color: 'orange', needsParams: 'password', tooltip: 'Cambia el PIN/password del device.' },
  { key: 3, label: 'Modo perdido', icon: <WarningOutlined />, dangerous: false, color: 'orange', needsParams: 'lostMode', tooltip: 'Activa modo perdido con mensaje y telefono de contacto.' },
  { key: 4, label: 'Salir perdido', icon: <SearchOutlined />, dangerous: false, color: undefined, needsParams: false, tooltip: 'Desactiva el modo perdido.' },
  { key: 5, label: 'Borrado total', icon: <DeleteOutlined />, dangerous: true, color: 'red', needsParams: false, tooltip: 'CUIDADO: borra el device y lo desenrola. Irreversible.' },
  { key: 6, label: 'Sincronizar', icon: <SyncOutlined />, dangerous: false, color: undefined, needsParams: false, tooltip: 'Pide a Google que reporte info actualizada del device.' },
  { key: 7, label: 'Limpiar app', icon: <ClearOutlined />, dangerous: true, color: 'orange', needsParams: 'clearApp', tooltip: 'Limpia los datos de una app especifica (sin desinstalarla).' },
] as const;

type ParamType = 'password' | 'lostMode' | 'clearApp' | false;

export function QuickCommandsBar({ device, onCommandSent }: Props) {
  const { modal, message } = App.useApp();
  const quickCmd = useQuickCommand();

  // Estado para los modales con parametros
  const [paramModal, setParamModal] = useState<{
    open: boolean;
    commandKey: number;
    paramType: ParamType;
    label: string;
  } | null>(null);

  const [paramInputs, setParamInputs] = useState<Record<string, string>>({});

  const isOnline = device.isEnrolledInGoogle && device.currentState === 1;

  const handleCommandClick = (def: typeof COMMAND_DEFS[number]) => {
    if (def.needsParams) {
      setParamInputs({});
      setParamModal({
        open: true,
        commandKey: def.key,
        paramType: def.needsParams,
        label: def.label,
      });
      return;
    }

    if (def.dangerous) {
      modal.confirm({
        title: `¿Confirmar comando "${def.label}"?`,
        content: def.tooltip,
        okText: 'Si, ejecutar',
        cancelText: 'Cancelar',
        okButtonProps: { danger: true },
        onOk: () => sendCommand(def.key),
      });
      return;
    }

    sendCommand(def.key);
  };

  const sendCommand = async (commandKey: number, paramsJson?: string) => {
    try {
      await quickCmd.mutateAsync({
        id: device.id,
        request: { commandType: commandKey, parametersJson: paramsJson },
      });
      onCommandSent();
      setParamModal(null);
    } catch {
      // interceptor maneja error
    }
  };

  const handleParamSubmit = () => {
    if (!paramModal) return;

    let paramsJson: string;
    switch (paramModal.paramType) {
      case 'password':
        if (!paramInputs.newPassword || paramInputs.newPassword.length < 4) {
          message.error('El PIN debe tener al menos 4 caracteres.');
          return;
        }
        paramsJson = JSON.stringify({ newPassword: paramInputs.newPassword });
        break;
      case 'lostMode':
        paramsJson = JSON.stringify({
          lostMessage: paramInputs.lostMessage || 'Este dispositivo ha sido reportado como perdido. Por favor contactar.',
          lostPhoneNumber: paramInputs.lostPhoneNumber || '',
        });
        break;
      case 'clearApp':
        if (!paramInputs.packageName) {
          message.error('Debe especificar el package name.');
          return;
        }
        paramsJson = JSON.stringify({ packageName: paramInputs.packageName });
        break;
      default:
        return;
    }

    sendCommand(paramModal.commandKey, paramsJson);
  };

  return (
    <Card title="Acciones rapidas" size="small" extra={
      !isOnline && <Text type="warning" style={{ fontSize: 12 }}>
        El device no esta enrolado en Google. Los comandos se encolaran pero no se enviaran.
      </Text>
    }>
      <Space wrap>
        {COMMAND_DEFS.map((def) => (
          <Tooltip key={def.key} title={def.tooltip}>
            <Button
              icon={def.icon}
              danger={def.dangerous && def.color === 'red'}
              onClick={() => handleCommandClick(def)}
              style={{
                ...(def.color === 'orange' && !def.dangerous ? { borderColor: '#fa8c16', color: '#fa8c16' } : {}),
              }}
              loading={quickCmd.isPending && paramModal?.commandKey !== def.key}
              disabled={quickCmd.isPending}
            >
              {def.label}
            </Button>
          </Tooltip>
        ))}
      </Space>

      {/* Modal de parametros */}
      {paramModal && (
        <Modal
          open={paramModal.open}
          title={`Parametros: ${paramModal.label}`}
          onCancel={() => setParamModal(null)}
          onOk={handleParamSubmit}
          confirmLoading={quickCmd.isPending}
          okText="Enviar comando"
          cancelText="Cancelar"
          destroyOnClose
        >
          {paramModal.paramType === 'password' && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>Nuevo PIN/password (minimo 4 caracteres):</Text>
              <Input.Password
                placeholder="Ej: 0321"
                maxLength={20}
                value={paramInputs.newPassword || ''}
                onChange={(e) => setParamInputs({ ...paramInputs, newPassword: e.target.value })}
                onPressEnter={handleParamSubmit}
                autoFocus
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                El device va a quedar con este PIN. Para Global Connect el PIN expected suele ser 0321.
              </Text>
            </Space>
          )}

          {paramModal.paramType === 'lostMode' && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>Mensaje a mostrar en pantalla:</Text>
              <Input.TextArea
                rows={3}
                placeholder="Este dispositivo ha sido reportado como perdido..."
                maxLength={500}
                value={paramInputs.lostMessage || ''}
                onChange={(e) => setParamInputs({ ...paramInputs, lostMessage: e.target.value })}
              />
              <Text>Telefono de contacto (opcional):</Text>
              <Input
                placeholder="+54 11 ..."
                value={paramInputs.lostPhoneNumber || ''}
                onChange={(e) => setParamInputs({ ...paramInputs, lostPhoneNumber: e.target.value })}
              />
            </Space>
          )}

          {paramModal.paramType === 'clearApp' && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>Package name de la app a limpiar:</Text>
              <Input
                placeholder="Ej: com.whatsapp"
                value={paramInputs.packageName || ''}
                onChange={(e) => setParamInputs({ ...paramInputs, packageName: e.target.value })}
                onPressEnter={handleParamSubmit}
                autoFocus
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Tip: el package name lo podes ver en el tab "Apps" de este device.
              </Text>
            </Space>
          )}
        </Modal>
      )}
    </Card>
  );
}
